import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Config } from '../config';
import { api } from '../services/api.js';

const AuthContext = createContext(null);

export default function AuthProvider({ children }) {
    const [token, setToken_] = useState(localStorage.getItem('token'));
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const setToken = (newToken) => {
        setToken_(newToken);
        if (newToken) {
            localStorage.setItem('token', newToken);
            axios.defaults.headers.common['access_token'] = `Bearer ${newToken}`;
            api.defaults.headers.common['access_token'] = `Bearer ${newToken}`;
        } else {
            localStorage.removeItem('token');
            delete axios.defaults.headers.common['access_token'];
            delete api.defaults.headers.common['access_token'];
        }
    };

    const logout = async () => {
        try {
            await axios.get(Config.APIBaseURL + '/auth/logout', {}, { withCredentials: true });
        } catch (error) {
            console.error('Error during logout:', error);
        } finally {
            setToken(null);
            localStorage.removeItem('token');
            localStorage.removeItem('userinfo');
            localStorage.removeItem('role');
            localStorage.removeItem('user_avatar');
            navigate('/login', { replace: true });
        }
    };

    // Initialize axios header if token exists and sync user role/profile
    useEffect(() => {
        const initializeAuth = async () => {
            if (token) {
                axios.defaults.headers.common['access_token'] = `Bearer ${token}`;
                api.defaults.headers.common['access_token'] = `Bearer ${token}`;
                try {
                    const response = await api.get('/user/me');
                    const userData = response.data;
                    if (userData) {
                        setUser(userData);
                        localStorage.setItem('role', userData.role || 'user');
                        if (userData.user_info) {
                            const mergedUserInfo = {
                                first_name: userData.user_info.first_name || '',
                                last_name: userData.user_info.last_name || '',
                                title: userData.user_info.title || ''
                            };
                            localStorage.setItem('userinfo', JSON.stringify(mergedUserInfo));
                        }
                    }
                } catch (error) {
                    if (error.response && error.response.status === 401) {
                        setToken(null);
                        localStorage.removeItem('role');
                        localStorage.removeItem('userinfo');
                        localStorage.removeItem('user_avatar');
                    }
                }
            } else {
                setUser(null);
                delete axios.defaults.headers.common['access_token'];
                delete api.defaults.headers.common['access_token'];
            }
            setLoading(false);
        };

        initializeAuth();
    }, [token]);

    const refreshUser = async () => {
        if (!token) return null;
        try {
            const response = await api.get('/user/me');
            const userData = response.data;
            if (userData) {
                setUser(userData);
                localStorage.setItem('role', userData.role || 'user');
                if (userData.user_info) {
                    const mergedUserInfo = {
                        first_name: userData.user_info.first_name || '',
                        last_name: userData.user_info.last_name || '',
                        title: userData.user_info.title || ''
                    };
                    localStorage.setItem('userinfo', JSON.stringify(mergedUserInfo));
                }
                return userData;
            }
        } catch (error) {
            console.error('Failed to refresh user:', error);
        }
        return null;
    };

    // Handle 401 responses globally — must be on the `api` instance, not global axios
    useEffect(() => {
        const interceptor = api.interceptors.response.use(
            response => response,
            error => {
                if (error.response && error.response.status === 401) {
                    setToken(null);
                    localStorage.removeItem('role');
                    localStorage.removeItem('userinfo');
                    localStorage.removeItem('user_avatar');
                    navigate('/login', { replace: true });
                }
                return Promise.reject(error);
            }
        );

        return () => api.interceptors.response.eject(interceptor);
    }, [navigate]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <AuthContext.Provider value={{
            token,
            user,
            setToken,
            logout,
            refreshUser,
            isAuthenticated: !!token
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
